import React, { FC, Fragment } from 'react';

import { DateTime } from 'luxon';

import { dateTimeFormatter, hockeyFormatter, stringFormatter, statFormatter } from '@nhl/formatter-utilities';
import { CoreEnums } from '@nhl/nhl-breakaway-ui';
import PlayerLink from '@nhl/nhl-react-ui/lib/PlayerLink';

import GlobalColumnFunctions from '~/utils/globalColumnFunctions';
import { formatInternationalTeam } from '~/views/pages/internationalTournament/utils';

import BaseColumnDefinitions, { CreateHeaderCell, createPlayerCol } from './baseColumnDefinitions';
import { GoalDetail, PlayerNameType } from './types';

const FOUR_NATION_TOURNAMENT_NAME = '4 Nations Face-Off';
export const FOUR_NATION_FACEOFF_2025 = 20242025;
const WORLD_CUP_OF_HOCKEY_2004 = 20032004;

export const formatInternationalSeason = (season: number): number => {
    // This is for the 2004 World Cup of Hockey which doesn't follow the same season format as the other tournaments
    if (season === WORLD_CUP_OF_HOCKEY_2004) {
        return 2004;
    } else if (season === FOUR_NATION_FACEOFF_2025) {
        return 2025;
    }
    return hockeyFormatter.seasonStartYearFormatter(season);
};

export const formatInternationalTournamentName = (gameTypeId: number, gameTypeDescription: string): string =>
    gameTypeId === CoreEnums.GameType.fourNations ? FOUR_NATION_TOURNAMENT_NAME : gameTypeDescription;

export const createTournamentTitle = (season: number, gameTypeId: number, gameTypeDescription: string): string =>
    `${formatInternationalSeason(season)} ${formatInternationalTournamentName(gameTypeId, gameTypeDescription)}`;

export const overtimeDisplay = (original: Record<string, any>, periodKey: string, showTies = false) => {
    // If the game didn't go to some kind of overtime we don't want to display anything
    if (!original.overtime) {
        return '';
    } else if (showTies && original.homeScore === original.visitingScore) {
        return 'T';
    } else if (original.gameFormat && original[periodKey]) {
        return hockeyFormatter.periodsFormatter(Number(original[periodKey]), original.gameFormat);
    }
    return original.overtime;
};

const PlayerName: FC<PlayerNameType> = ({ original, playerIdKey, firstNameKey, lastNameKey, activePlayerKey }) => {
    const player = {
        ...original,
        id: original[playerIdKey],
        firstName: original[firstNameKey],
        lastName: original[lastNameKey],
    };
    return player.lastName ? (
        <PlayerLink
            player={player}
            title={original[activePlayerKey] ? 'Active Player' : null}
            className={original[activePlayerKey] ? 'strong' : ''}
        />
    ) : (
        '--'
    );
};

const nameColumn = (header, playerIdKey, firstNameKey, lastNameKey, activePlayerKey = 'isActive') => ({
    Header: CreateHeaderCell(header),
    headerClassName: 'name left',
    className: 'name left',
    accessor: lastNameKey,
    Cell: ({ original }) => (
        <PlayerName
            activePlayerKey={activePlayerKey}
            firstNameKey={firstNameKey}
            lastNameKey={lastNameKey}
            original={original}
            playerIdKey={playerIdKey}
        />
    ),
    sortType: (a, b) => {
        const player1LastName = a.original[lastNameKey].toUpperCase();
        const player2LastName = b.original[lastNameKey].toUpperCase();
        if (player1LastName === player2LastName) {
            const player1FirstName = a.original[lastNameKey].toUpperCase();
            const player2FirstName = b.original[lastNameKey].toUpperCase();
            return player1FirstName.localeCompare(player2FirstName);
        }
        return player1LastName.localeCompare(player2LastName);
    },
    minWidth: window.innerWidth > 414 ? 150 : 130,
    getProps: GlobalColumnFunctions.getProps,
});

const teamColumn = (teamNames) => {
    if (!teamNames) {
        return;
    }

    const teams: Array<string> = [];
    teamNames.split(',').map((teamName) => {
        const name = formatInternationalTeam(teamName);
        if (!teams.includes(name)) {
            teams.push(name);
        }
    });

    return teams.map((teamName, index) => {
        if (index === teams.length - 1) {
            return <Fragment key={teamName}>{teamName}</Fragment>;
        }
        return <div key={teamName}>{teamName},&nbsp;</div>;
    });
};

const PLAYER_NAME = createPlayerCol({ activePlayerKey: 'isActive' });

const TEAM_NAME = {
    ...BaseColumnDefinitions.TEAMS_COL,
    accessor: 'teamName',
    Cell: ({ original }) => teamColumn(original.teamName),
    Header: BaseColumnDefinitions.CreateHeaderCell('Team', 'Team', { textAlign: 'left', width: '100%' }),
    maxWidth: 220,
};

const TEAM_NAMES = {
    ...BaseColumnDefinitions.TEAMS_COL,
    accessor: 'teamName',
    Cell: ({ original }) => teamColumn(original.teamName),
    Header: BaseColumnDefinitions.CreateHeaderCell('Teams', 'Team(s)', { textAlign: 'left', width: '100%' }),
};

const CHAMPIONSHIP_APPEARANCES = {
    accessor: 'championshipAppearances',
    getProps: GlobalColumnFunctions.getProps,
    Header: BaseColumnDefinitions.CreateHeaderCell('Championship Appearances'),
};

const CHAMPIONSHIPS = {
    accessor: 'championships',
    getProps: GlobalColumnFunctions.getProps,
    Header: BaseColumnDefinitions.CreateHeaderCell('Championships'),
    minWidth: 120,
};

const SAVES = {
    accessor: 'saves',
    getProps: GlobalColumnFunctions.getProps,
    Header: BaseColumnDefinitions.CreateHeaderCell('Sv'),
};

const DATE = {
    Header: CreateHeaderCell('Date'),
    accessor: 'date',
    Cell: ({ original }) => DateTime.fromISO(original.date).toLocaleString(DateTime.DATE_MED),
    getProps: GlobalColumnFunctions.getProps,
    minWidth: 80,
};

const GAME_WINNING_GOALS = {
    accessor: 'gameWinningGoals',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Game Winning Goals', 'GWG'),
};

const GAME_WINNING_GOAL_SHOOTER = nameColumn(
    'GWG',
    'gameWinningGoalShooterId',
    'gameWinningGoalShooterFirstName',
    'gameWinningGoalShooterLastName',
    'isActiveGameWinningGoalShooter'
);

const gameInfoColumn = (opponentTriCode: string) => ({
    Header: CreateHeaderCell('Game'),
    accessor: 'gameId',
    Cell: ({ original }) => {
        const gameInfo = {
            ...original,
            gameDate: original.date,
            opponentTriCode: original[opponentTriCode],
        };
        return (
            <a href={`https://www.nhl.com/gamecenter/${original.gameId}`} target="_blank">
                {BaseColumnDefinitions.getBoxScoreContent(gameInfo, overtimeDisplay(original, 'totalPeriods'))}
            </a>
        );
    },
    minWidth: window.innerWidth > 414 ? 150 : 130,
    getProps: GlobalColumnFunctions.getProps,
});

const GAME_INFO = gameInfoColumn('opponentTriCode');
const GAME_INFO_PENALTY_SHOT = gameInfoColumn('goalieTricode');

const GOALTENDER = nameColumn('Goaltender', 'goalieId', 'goalieFirstName', 'goalieLastName', 'isActiveGoalie');

const GOALTENDER_TEAM = {
    accessor: 'goalieTeamName',
    Cell: ({ original }) => teamColumn(original.goalieTeamName),
    Header: BaseColumnDefinitions.CreateHeaderCell('Goaltender Team', 'Goaltender Team', { textAlign: 'left' }),
    style: { justifyContent: 'left', textAlign: 'left', flexFlow: 'wrap', whiteSpace: 'pre-line' },
};

const GoalDetails: FC<GoalDetail> = ({ original, scorerNumber, withPlayerName = false, withTriCode = false }) => {
    const firstNameKey = `scorer${scorerNumber}FirstName`;
    const lastNameKey = `scorer${scorerNumber}LastName`;

    const teamId = original[`scorer${scorerNumber}TeamId`];
    const triCode = teamId === original.homeTeamId ? original.homeTriCode : original.visitingTriCode;

    const secondsElapsedInPeriod = original[`goal${scorerNumber}SecondsElapsedInPeriod`];
    const goalPeriodValue = original[`goal${scorerNumber}Period`];
    const goalPeriod =
        goalPeriodValue && original.gameFormat
            ? hockeyFormatter.periodsFormatter(goalPeriodValue, original.gameFormat)
            : goalPeriodValue;
    const goalDetail = `${dateTimeFormatter.displaySecondsFormatted(secondsElapsedInPeriod)} (${goalPeriod})`;
    return (
        <div>
            {withPlayerName && original[firstNameKey] && original[lastNameKey] && (
                // Player name is wrapped in a div to ensure the player is display on their own line
                // This is needed for non-NHL players where only a string is displayed
                <div>
                    <PlayerName
                        activePlayerKey={`isActiveScorer${scorerNumber}`}
                        playerIdKey={`scorer${scorerNumber}Id`}
                        firstNameKey={firstNameKey}
                        lastNameKey={lastNameKey}
                        original={original}
                    />
                </div>
            )}
            {withTriCode && `${triCode} - `}
            {goalDetail}
        </div>
    );
};

// Accessor is set to seconds by game for sorting purposes, but display value is seconds by period
const PERIOD_COL = {
    accessor: 'period',
    Cell: ({ original, value }) =>
        value && original.gameFormat ? hockeyFormatter.periodsFormatter(value, original.gameFormat) : value,
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Period'),
};

const GOAL1 = (withPlayerName = false, withTriCode = false) => ({
    accessor: 'goal1SecondsElapsedInGame',
    Cell: ({ original }) => (
        <GoalDetails original={original} scorerNumber={1} withPlayerName={withPlayerName} withTriCode={withTriCode} />
    ),
    Header: BaseColumnDefinitions.CreateHeaderCell('Goal 1', 'G1'),
});

const GOAL2 = (withPlayerName = false, withTriCode = false) => ({
    accessor: 'goal2SecondsElapsedInGame',
    Cell: ({ original }) => (
        <GoalDetails original={original} scorerNumber={2} withPlayerName={withPlayerName} withTriCode={withTriCode} />
    ),
    Header: BaseColumnDefinitions.CreateHeaderCell('Goal 2', 'G2'),
});

const GOAL3 = (withPlayerName = false, withTriCode = false) => ({
    accessor: 'goal3SecondsElapsedInGame',
    Cell: ({ original }) => (
        <GoalDetails original={original} scorerNumber={3} withPlayerName={withPlayerName} withTriCode={withTriCode} />
    ),
    Header: BaseColumnDefinitions.CreateHeaderCell('Goal 3', 'G3'),
});

const GOAL_BREAKDOWN = {
    accessor: 'homeGoals',
    Cell: ({ original }) =>
        `${original.visitingTriCode} ${original.visitingGoals}, ${original.homeTriCode} ${original.homeGoals}`,
    Header: BaseColumnDefinitions.CreateHeaderCell('Breakdown'),
};

const GOAL_PP_BREAKDOWN = {
    accessor: 'homePowerPlayGoals',
    Cell: ({ original }) =>
        `${original.visitingTriCode} ${original.visitingPowerPlayGoals}, ${original.homeTriCode} ${original.homePowerPlayGoals}`,
    Header: BaseColumnDefinitions.CreateHeaderCell('Breakdown'),
};

const OPPONENT_TEAM = {
    accessor: 'opponentTeamName',
    Cell: ({ original }) => teamColumn(original.opponentTeamName),
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Opponent Team'),
    style: { justifyContent: 'left', textAlign: 'left', flexFlow: 'wrap', whiteSpace: 'pre-line' },
};

const OVERTIME_LENGTH = {
    accessor: 'secondsElapsedInGame',
    Cell: ({ original }) => {
        const regulationTime = original.gameFormat.regulationPeriods * original.gameFormat.regulationPeriodSeconds;
        return dateTimeFormatter.displaySecondsFormatted(original.secondsElapsedInGame - regulationTime);
    },
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('OT Length'),
    maxWidth: 140,
};

const OVERTIME_PERIODS = {
    ...PERIOD_COL,
    accessor: 'totalPeriods',
    Header: CreateHeaderCell('OT Period(s)'),
    maxWidth: 140,
};

const POSITION = {
    accessor: 'position',
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Position', 'Pos'),
    minWidth: 100,
    maxWidth: 140,
};

const RESULT = {
    accessor: 'gameId',
    Header: CreateHeaderCell('Result'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ original }) => {
        const isHomeTeam = original.homeTeamId === original.teamId;
        const homeTeam = original.homeTriCode || (isHomeTeam ? original.triCode : original.opponentTriCode);
        const visitingTeam = original.visitingTriCode || (!isHomeTeam ? original.triCode : original.opponentTriCode);
        const overtime = overtimeDisplay(original, 'totalPeriods', true);

        return (
            <a href={`https://www.nhl.com/gamecenter/${original.gameId}`} target="_blank">
                {`${visitingTeam} ${original.visitingScore} @ ${homeTeam} ${original.homeScore} ${overtime}`}
            </a>
        );
    },
    disableSortBy: true,
};

const SCORED = {
    accessor: 'didScore',
    Cell: ({ value }) => (value ? 'Y' : ''),
    Header: 'Scored',
    maxWidth: 80,
};

const SECONDS_ELAPSED_IN_GAME = {
    accessor: 'secondsElapsedInGame',
    Cell: ({ value }) => dateTimeFormatter.displaySecondsFormatted(value),
    getProps: GlobalColumnFunctions.getProps,
    Header: 'Time',
};

const SECONDS_ELAPSED_IN_PERIOD = {
    accessor: 'secondsElapsedInPeriod',
    Cell: ({ value }) => dateTimeFormatter.displaySecondsFormatted(value),
    getProps: GlobalColumnFunctions.getProps,
    Header: 'Time',
};

const SHOOTER = nameColumn('Player', 'shooterId', 'shooterFirstName', 'shooterLastName', 'isActiveShooter');

const SHOOTER_TEAM = {
    ...TEAM_NAME,
    accessor: 'shooterTeamName',
    Cell: ({ original }) => teamColumn(original.shooterTeamName),
    Header: BaseColumnDefinitions.CreateHeaderCell('Shooter Team', 'Shooter Team', { textAlign: 'left' }),
};

const SHOT_BREAKDOWN = {
    accessor: 'homeShots',
    Cell: ({ original }) =>
        `${original.visitingTriCode} ${original.visitingShots}, ${original.homeTriCode} ${original.homeShots}`,
    Header: BaseColumnDefinitions.CreateHeaderCell('Breakdown'),
};

const SHOT_TIME = {
    accessor: 'shotTime',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Time'),
    maxWidth: 80,
};

const TIME_IN_PERIOD = {
    accessor: 'timeInPeriod',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Time'),
};

const TIME_BETWEEN_GOALS = {
    accessor: 'timeBetweenGoals',
    Cell: ({ value }) => dateTimeFormatter.displaySecondsFormatted(value),
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Time'),
    maxWidth: 80,
};

const FASTEST_TWO_GOALS_TIME_FROM_START_PERIOD = {
    accessor: 'goal2TimeInPeriod',
    Cell: ({ value }) => value,
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Time'),
    maxWidth: 80,
};

const TOTAL_GOALS_COL = {
    accessor: 'totalGoals',
    Header: CreateHeaderCell('Goals', 'G'),
    getProps: GlobalColumnFunctions.getProps,
    sortDescFirst: true,
};

const TOTAL_SHOTS_COL = {
    accessor: 'totalShots',
    Header: CreateHeaderCell('Shots', 'S'),
    getProps: GlobalColumnFunctions.getProps,
    sortDescFirst: true,
};

const TOTAL_PP_GOALS_COL = {
    accessor: 'totalPowerPlayGoals',
    Header: CreateHeaderCell('Total Power Play Goals', 'PPG'),
    getProps: GlobalColumnFunctions.getProps,
    sortDescFirst: true,
};

const TOURNAMENT_NAME = {
    accessor: 'gameTypeDescription',
    Cell: ({ original }) => createTournamentTitle(original.season, original.gameType, original.gameTypeDescription),
    getProps: GlobalColumnFunctions.getProps,
    Header: BaseColumnDefinitions.CreateHeaderCell('Tournament'),
    minWidth: 200,
    maxWidth: 240,
    sortType: (a, b) => {
        if (a.original.season === b.original.season) {
            const name1 = formatInternationalTournamentName(a.original.gameType, a.original.gameTypeDescription);
            const name2 = formatInternationalTournamentName(b.original.gameType, b.original.gameTypeDescription);
            return name1.localeCompare(name2);
        }
        return a.original.season - b.original.season;
    },
};

const SERIES_TITLE = {
    accessor: 'seriesTitle',
    Header: BaseColumnDefinitions.CreateHeaderCell('Round'),
    minWidth: 120,
    maxWidth: 160,
};

const TOURNAMENTS_PLAYED = {
    accessor: 'tournamentsPlayed',
    getProps: GlobalColumnFunctions.getProps,
    Header: BaseColumnDefinitions.CreateHeaderCell('Tournaments Played'),
    minWidth: 110,
};

const YEARS_WON = {
    accessor: 'championshipSeasons',
    Cell: ({ value }) => {
        const seasons = value
            .split(',')
            .map((season) => <div key={season}>{formatInternationalSeason(Number(season.trim()))}</div>);
        return <div>{seasons}</div>;
    },
    getProps: GlobalColumnFunctions.getProps,
    Header: BaseColumnDefinitions.CreateHeaderCell('Years Won'),
};

const GOALS_AGAINST_AVERAGE_COL = {
    accessor: 'goalsAgainstAverage',
    Header: CreateHeaderCell('Goals Against Average', 'GAA'),
    Cell: ({ row, value }) => statFormatter.displayValueAs('goalsAgainstAverage')(value, row.original.shotsAgainst > 0),
    getProps: GlobalColumnFunctions.getProps,
    sortType: (a, b) => {
        const value1 = statFormatter.displayValueAs('goalsAgainstAverage')(
            a.original.goalsAgainstAverage,
            a.original.shotsAgainst > 0
        );
        const value2 = statFormatter.displayValueAs('goalsAgainstAverage')(
            b.original.goalsAgainstAverage,
            b.original.shotsAgainst > 0
        );
        return Number(value1) - Number(value2);
    },
};

const SAVE_PCTG_COL = {
    accessor: 'savePctg',
    Header: CreateHeaderCell('Save %', 'Sv%'),
    Cell: ({ value }) => statFormatter.displayValueAs('savePctg')(value),
    getProps: GlobalColumnFunctions.getProps,
    sortType: (a, b) => {
        const value1 = statFormatter.displayValueAs('savePctg')(a.original.savePctg);
        const value2 = statFormatter.displayValueAs('savePctg')(b.original.savePctg);
        return Number(value1) - Number(value2);
    },
};

const WINNING_TEAM = {
    accessor: 'winningTeamName',
    Cell: ({ original }) => teamColumn(original.winningTeamName),
    Header: CreateHeaderCell('Winning Team'),
    getProps: GlobalColumnFunctions.getProps,
    sortType: 'basic',
};

const WINNING_CAPTAIN = {
    ...nameColumn(
        'Captain',
        'winningCaptainId',
        'winningCaptainFirstName',
        'winningCaptainLastName',
        'isActiveWinningCaptain'
    ),
    headerClassName: 'name center',
    className: 'name center',
};

const LOSING_TEAM = {
    accessor: 'losingTeamName',
    Cell: ({ original }) => teamColumn(original.losingTeamName),
    Header: CreateHeaderCell('Runner-Up'),
    getProps: GlobalColumnFunctions.getProps,
    sortType: 'basic',
};

const LOSING_CAPTAIN = {
    ...nameColumn(
        'Captain',
        'losingCaptainId',
        'losingCaptainFirstName',
        'losingCaptainLastName',
        'isActiveLosingCaptain'
    ),
    headerClassName: 'name center',
    className: 'name center',
};

const WINNING_COACH = {
    accessor: 'homeCoach',
    Header: CreateHeaderCell('Head Coach'),
    getProps: GlobalColumnFunctions.getProps,
    sortType: 'basic',
    Cell: ({ original }) => (original.homeTeamId === original.winningTeamId ? original.homeCoach : original.awayCoach),
};

const LOSING_COACH = {
    ...WINNING_COACH,
    id: 'losingCoach',
    Cell: ({ original }) => (original.homeTeamId === original.winningTeamId ? original.awayCoach : original.homeCoach),
};

const FINAL_HOST_CITY = {
    accessor: 'city',
    Cell: ({ original }) => `${original.city}, ${original.countryCode}`,
    Header: CreateHeaderCell('Final Host'),
    getProps: GlobalColumnFunctions.getProps,
    sortType: 'basic',
};

const GWG_SCORER = nameColumn(
    'GWG Scorer',
    'gameWinningGoalShooterId',
    'gameWinningGoalShooterFirstName',
    'gameWinningGoalShooterLastName',
    'isActiveGameWinningGoalShooter'
);

const GWG_TIME = {
    accessor: 'gameWinningGoalTime',
    Header: CreateHeaderCell('Time of Goal'),
    getProps: GlobalColumnFunctions.getProps,
    sortType: 'basic',
};

const WINNING_GOALIE = nameColumn(
    'Winning Goaltender',
    'winningGoalieId',
    'winningGoalieFirstName',
    'winningGoalieLastName',
    'isActiveWinningGoalie'
);

export default {
    CHAMPIONSHIP_APPEARANCES,
    CHAMPIONSHIPS,
    DATE,
    GAME_INFO,
    GAME_INFO_PENALTY_SHOT,
    GAME_WINNING_GOALS,
    GAME_WINNING_GOAL_SHOOTER,
    GOAL1,
    GOAL2,
    GOAL3,
    GOAL_BREAKDOWN,
    GOAL_PP_BREAKDOWN,
    GOALTENDER,
    GOALTENDER_TEAM,
    OPPONENT_TEAM,
    OVERTIME_LENGTH,
    OVERTIME_PERIODS,
    PERIOD_COL,
    PLAYER_NAME,
    POSITION,
    RESULT,
    SCORED,
    SECONDS_ELAPSED_IN_GAME,
    SECONDS_ELAPSED_IN_PERIOD,
    SERIES_TITLE,
    SHOOTER,
    SHOOTER_TEAM,
    SHOT_BREAKDOWN,
    SHOT_TIME,
    TEAM_NAME,
    TEAM_NAMES,
    TIME_IN_PERIOD,
    TIME_BETWEEN_GOALS,
    FASTEST_TWO_GOALS_TIME_FROM_START_PERIOD,
    TOTAL_GOALS_COL,
    TOTAL_SHOTS_COL,
    TOTAL_PP_GOALS_COL,
    TOURNAMENT_NAME,
    TOURNAMENTS_PLAYED,
    YEARS_WON,
    SAVES,
    GOALS_AGAINST_AVERAGE_COL,
    SAVE_PCTG_COL,
    WINNING_TEAM,
    WINNING_CAPTAIN,
    LOSING_TEAM,
    LOSING_CAPTAIN,
    WINNING_COACH,
    LOSING_COACH,
    FINAL_HOST_CITY,
    GWG_SCORER,
    GWG_TIME,
    WINNING_GOALIE,
};
